/** Per-user game history: the list of finished (and in-progress) runs, newest first. Each finished run
 *  expands to an answer-by-answer review (what you chose and how it moved each meter), via RunTranscript. */
import { useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useRun, useRuns } from "../../api/runs";
import { gameContent } from "../../content";
import { PageShell } from "../PageShell";
import { RunTranscript } from "../../components/RunTranscript";
import { cx } from "../../lib/ui";
import type { TranscriptEntry } from "../../game/machine";
import type { Scores } from "../../game/scoring";

const SHORT: Record<string, string> = {
  dealSecurity: "Deal",
  clientSatisfaction: "Client",
  counterpartyCooperation: "Counterparty",
  transactionProcess: "Process",
};

export function HistoryPage() {
  const runs = useRuns(true);
  const [openId, setOpenId] = useState<string | null>(null);
  const detail = useRun(openId);
  const endingLabel = (id: string | null) =>
    gameContent.endings.find((e) => e.id === id)?.label ?? "Outcome recorded";

  return (
    <PageShell>
      <h1 className="font-display text-fluid-xl font-semibold text-ink">Your games</h1>
      <p className="mt-1 text-ink-600">Every game you finish is saved here — open one to review your answers.</p>

      {runs.isLoading && <p className="mt-6 text-ink-500">Loading…</p>}
      {runs.data && runs.data.length === 0 && (
        <p className="mt-6 text-ink-500">
          No games yet —{" "}
          <Link to="/" className="text-brand underline-offset-4 hover:underline">
            play your first
          </Link>
          .
        </p>
      )}

      <ul className="mt-6 flex flex-col gap-3">
        {runs.data?.map((r) => {
          const open = openId === r.id;
          const finished = r.status === "finished";
          return (
            <li key={r.id} className="rounded-sm border border-ink-150 bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-medium text-ink">{finished ? endingLabel(r.endingId) : "In progress"}</p>
                  <p className="text-sm text-ink-500">
                    {new Date(r.finishedAt ?? r.updatedAt).toLocaleString()} ·{" "}
                    {r.mode === "untimed" ? "Practice" : "Timed"}
                  </p>
                </div>
                {r.scores && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-ink-600">
                    {gameContent.meta.dimensions.map((d) => (
                      <span key={d.id} className="tabular-nums">
                        {SHORT[d.id] ?? d.label} <span className="font-mono text-ink">{r.scores?.[d.id] ?? "—"}</span>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {finished && (
                <div className="mt-3 border-t border-ink-150 pt-3">
                  <button
                    type="button"
                    onClick={() => setOpenId(open ? null : r.id)}
                    aria-expanded={open}
                    className="inline-flex items-center gap-1 text-sm font-medium text-brand hover:underline"
                  >
                    {open ? "Hide review" : "Review your answers"}
                    <ChevronDown
                      size={16}
                      className={cx("transition-transform", open && "rotate-180")}
                      aria-hidden="true"
                    />
                  </button>
                  {open && (
                    <div className="mt-4">
                      {detail.isLoading && <p className="text-ink-500">Loading…</p>}
                      {detail.data && (
                        <RunTranscript
                          transcript={
                            (Array.isArray(detail.data.transcript) ? detail.data.transcript : []) as TranscriptEntry[]
                          }
                          scores={detail.data.scores as Scores | null}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </PageShell>
  );
}
